import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Trophy, Star, BookOpen, CalendarCheck, Award, Medal, X } from 'lucide-react';

const GRAD_CAP = "https://static.prod-images.emergentagent.com/jobs/22029687-1289-4783-879e-410ec2896cc4/images/cd950c6813bcde0516be129f7c2fe4ae9766c55cd86a76f55b4c3f07c7f8e656.png";

const BADGE_META = {
  quiz_master:        { name: 'Quiz Master',        desc: 'Scored 90% or more on a quiz!', icon: Trophy,        color: '#FFD700' },
  first_quiz:         { name: 'First Steps',         desc: 'Completed the first quiz!',      icon: Star,          color: '#87CEEB' },
  lesson_hero:        { name: 'Lesson Hero',         desc: '10 lessons done — way to go!',    icon: BookOpen,      color: '#8DB600' },
  perfect_attendance: { name: 'Perfect Attendance',  desc: '10 classes in a row!',            icon: CalendarCheck, color: '#FD5E53' },
  points_100:         { name: 'Centurion',           desc: 'Earned 100 points!',              icon: Award,         color: '#FFD700' },
  points_500:         { name: 'Champion',            desc: 'Earned 500 points!',              icon: Medal,         color: '#FD5E53' },
};

function fireConfetti() {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ['#FD5E53', '#FDE047', '#8DB600', '#87CEEB', '#F59E0B'];

  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  // Big burst at start
  confetti({
    particleCount: 120,
    spread: 80,
    origin: { y: 0.6 },
    colors,
    scalar: 1.1,
  });
}

export default function BadgeCelebration({ badges, onClose }) {
  useEffect(() => {
    if (badges && badges.length > 0) fireConfetti();
  }, [badges]);

  if (!badges || badges.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="badge-celebration"
      >
        <motion.div
          initial={{ scale: 0.7, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 180, damping: 20 }}
          className="relative bg-white rounded-[2.5rem] p-8 sm:p-10 max-w-md w-full text-center shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Gradient backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] opacity-70 pointer-events-none" />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/40 text-slate-600 z-10"
            data-testid="celebration-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative z-10">
            <motion.img
              src={GRAD_CAP}
              alt="Graduation cap"
              initial={{ y: -20, rotate: -15, scale: 0 }}
              animate={{ y: 0, rotate: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-4 object-contain drop-shadow-xl"
            />

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="font-nunito text-xs font-bold uppercase tracking-widest text-[#FD5E53] mb-2"
            >
              {badges.length > 1 ? `${badges.length} New Badges!` : 'New Badge Earned!'}
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="font-fredoka text-3xl sm:text-4xl font-semibold text-slate-800 mb-5 tracking-tight"
            >
              Woohoo! 🎉
            </motion.h2>

            <div className="space-y-2.5 mb-6">
              {badges.map((id, i) => {
                const meta = BADGE_META[id] || { name: id, desc: 'Nice work!', icon: Trophy, color: '#FD5E53' };
                const Icon = meta.icon;
                return (
                  <motion.div
                    key={id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.55 + i * 0.1 }}
                    className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-2xl p-3 text-left"
                    data-testid={`celebration-badge-${id}`}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${meta.color}33` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <p className="font-fredoka font-medium text-slate-800 text-sm">{meta.name}</p>
                      <p className="font-nunito text-xs text-slate-500">{meta.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 + badges.length * 0.1 + 0.2 }}
              onClick={onClose}
              className="bg-[#FD5E53] hover:bg-[#E84E44] text-white px-7 py-3 rounded-full font-fredoka font-medium shadow-md transition-all hover:-translate-y-0.5"
              data-testid="celebration-awesome-btn"
            >
              Awesome! ✨
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
